/**
 * walkthroughSteps.js
 *
 * All guided tour step definitions.
 * Each step maps to a data-tour attribute on a DOM element and declares
 * which route it belongs to for automatic navigation.
 *
 * Titles are kept concise and label-like (no emoji, no exclamation marks)
 * to match the enterprise tone of Linear, Notion, and Vercel onboarding.
 */

export const TOUR_STEPS = [
  // ── Step 0: Navigation Bar ─────────────────────────────────────────────────
  {
    target: '[data-tour="nav-bar"]',
    title: 'Main Navigation',
    content:
      'Use the navigation bar to move between the Marketplace, Dashboard, Workflows, and Knowledge Base at any time.',
    placement: 'bottom',
    route: '/dashboard',
    disableBeacon: true,
    spotlightClicks: false,
  },

  // ── Step 1: Dashboard Header ───────────────────────────────────────────────
  {
    target: '[data-tour="dashboard-header"]',
    title: 'Your Dashboard',
    content:
      'Your command center — see your current plan, remaining credits, and a high-level overview of your AI agent activity at a glance.',
    placement: 'bottom',
    route: '/dashboard',
    disableBeacon: true,
    spotlightClicks: false,
  },

  // ── Step 2: Stats Grid ─────────────────────────────────────────────────────
  {
    target: '[data-tour="stats-grid"]',
    title: 'Usage Overview',
    content:
      'Monitor credits remaining, total queries sent, credits used this billing cycle, and the number of unique agents you\'ve interacted with — all updated in real time.',
    placement: 'bottom',
    route: '/dashboard',
    disableBeacon: true,
    spotlightClicks: false,
  },

  // ── Step 3: Quick Actions ──────────────────────────────────────────────────
  {
    target: '[data-tour="quick-actions"]',
    title: 'Quick Actions',
    content:
      'Shortcuts to your most common tasks. Jump directly to the Marketplace to explore agents, or view available plans to expand your capabilities.',
    placement: 'left',
    route: '/dashboard',
    disableBeacon: true,
    spotlightClicks: false,
  },

  // ── Step 4: Marketplace Header ─────────────────────────────────────────────
  {
    target: '[data-tour="marketplace-header"]',
    title: 'Agent Marketplace',
    content:
      'Discover 30+ specialized AI agents built for real business workflows — from Legal Contract Review to BI Data Storytelling. Every agent is powered by state-of-the-art LLMs.',
    placement: 'bottom',
    route: '/marketplace',
    disableBeacon: true,
    spotlightClicks: false,
  },

  // ── Step 5: Marketplace Search ─────────────────────────────────────────────
  {
    target: '[data-tour="marketplace-search"]',
    title: 'Search Agents',
    content:
      'Find any agent instantly by name, category, or capability. Try searching "HR", "Finance", or "Legal" to see relevant results.',
    placement: 'bottom',
    route: '/marketplace',
    disableBeacon: true,
    spotlightClicks: true,
  },

  // ── Step 6: Category Filter ────────────────────────────────────────────────
  {
    target: '[data-tour="marketplace-category"]',
    title: 'Filter by Industry',
    content:
      'Browse agents by industry using the category menu. Select an industry, business function, and specific agent type to narrow down results for your use case.',
    placement: 'bottom',
    route: '/marketplace',
    disableBeacon: true,
    spotlightClicks: true,
  },

  // ── Step 7: Agent Card ─────────────────────────────────────────────────────
  {
    target: '[data-tour="agent-card-first"]',
    title: 'Agent Cards',
    content:
      'Each card shows the agent\'s name, category, tier (Free, Pro, or Enterprise), and cost per query. Click any card to open the agent and start a conversation immediately.',
    placement: 'top',
    route: '/marketplace',
    disableBeacon: true,
    spotlightClicks: true,
  },

  // ── Step 8: Workflow List Header ───────────────────────────────────────────
  {
    target: '[data-tour="workflow-list-header"]',
    title: 'FLUX Workflow Engine',
    content:
      'Chain multiple AI agents together into automated workflows. Trigger them manually, on a schedule, or via webhook — ideal for multi-step business processes.',
    placement: 'bottom',
    route: '/workflow',
    disableBeacon: true,
    spotlightClicks: false,
  },

  // ── Step 9: Workflow Stats ─────────────────────────────────────────────────
  {
    target: '[data-tour="workflow-stats-grid"]',
    title: 'Workflow Overview',
    content:
      'At a glance: total workflows created, how many are currently active, how many run on a schedule, and how many are triggered via webhook.',
    placement: 'bottom',
    route: '/workflow',
    disableBeacon: true,
    spotlightClicks: false,
  },

  // ── Step 10: New Workflow Button ───────────────────────────────────────────
  {
    target: '[data-tour="workflow-new-btn"]',
    title: 'Create a Workflow',
    content:
      'Open the visual workflow editor. From here you can build automated pipelines by dragging and dropping AI nodes onto the canvas.',
    placement: 'left',
    route: '/workflow',
    disableBeacon: true,
    spotlightClicks: false,
  },

  // ── Step 11: Workflow Editor — Top Bar ─────────────────────────────────────
  {
    target: '[data-tour="editor-topbar"]',
    title: 'Workflow Editor',
    content:
      'Name your workflow, choose a trigger type (Manual, Webhook, or Schedule), and toggle the Active state. All changes are reflected live as you build.',
    placement: 'bottom',
    route: '/workflow/new',
    disableBeacon: true,
    spotlightClicks: false,
  },

  // ── Step 12: Node Sidebar ──────────────────────────────────────────────────
  {
    target: '[data-tour="editor-node-sidebar"]',
    title: 'Node Library',
    content:
      'Browse available node types grouped by category — Triggers, Agents, AI models, Data, Notifications, and more. Drag any node onto the canvas to add it to your workflow.',
    placement: 'right',
    route: '/workflow/new',
    disableBeacon: true,
    spotlightClicks: false,
  },

  // ── Step 13: Canvas ────────────────────────────────────────────────────────
  {
    target: '[data-tour="editor-canvas"]',
    title: 'Workflow Canvas',
    content:
      'Connect nodes by dragging from one output handle to another node\'s input. Click any node to configure its settings. Use the minimap in the bottom-left to navigate large workflows.',
    placement: 'left',
    route: '/workflow/new',
    disableBeacon: true,
    spotlightClicks: false,
  },

  // ── Step 14: AI Generate ──────────────────────────────────────────────────
  {
    target: '[data-tour="editor-ai-btn"]',
    title: 'AI Workflow Generator',
    content:
      'Describe your automation goal in plain language and the AI will generate a complete workflow for you — nodes, connections, and configuration included.',
    placement: 'bottom',
    route: '/workflow/new',
    disableBeacon: true,
    spotlightClicks: false,
  },

  // ── Step 15: Save & Run ────────────────────────────────────────────────────
  {
    target: '[data-tour="editor-save-btn"]',
    title: 'Save & Run',
    content:
      'Save your workflow to persist all changes. Once saved, use the Run button to execute it immediately and monitor each node\'s progress in real time.',
    placement: 'bottom',
    route: '/workflow/new',
    disableBeacon: true,
    spotlightClicks: false,
  },

  // ── Step 16: Billing / Plans ───────────────────────────────────────────────
  {
    target: '[data-tour="billing-plans"]',
    title: 'Plans & Billing',
    content:
      'Review and upgrade your subscription here. The Free plan includes 100 credits; Pro gives 5,000; Enterprise unlocks premium agents and advanced features.',
    placement: 'top',
    route: '/dashboard',
    disableBeacon: true,
    spotlightClicks: false,
  },

  // ── Step 17: Settings / LLM Preference ────────────────────────────────────
  {
    target: '[data-tour="settings-llm"]',
    title: 'Model Preferences',
    content:
      'Choose the AI model used across all agents. Switch between the default Llama model, IBM Watsonx, or OpenAI — giving you full control over your AI stack.',
    placement: 'right',
    route: '/dashboard',
    disableBeacon: true,
    spotlightClicks: true,
  },
];

/** Routes that steps belong to, for navigation logic */
export const STEP_ROUTES = TOUR_STEPS.map((s) => s.route);

/** Total number of steps (excluding welcome + completion modals) */
export const TOTAL_STEPS = TOUR_STEPS.length;
